import { useCallback, useMemo, useState } from "react"
import type { Locale } from "@/shared/lib/i18n"

export type SearchLanguagesEditor = {
	selected: Set<Locale>
	toggle: (locale: Locale) => void
	isLastEnabled: boolean
	isDirty: boolean
	commit: () => Promise<void>
}

function setsEqual<T>(a: Set<T>, b: Set<T>): boolean {
	if (a.size !== b.size) return false
	for (const value of a) if (!b.has(value)) return false
	return true
}

export function useSearchLanguagesEditor({
	initial,
	onSave,
}: {
	initial: Locale[]
	onSave: (next: Locale[]) => Promise<void>
}): SearchLanguagesEditor {
	const initialSet = useMemo(() => new Set(initial), [initial])
	const [selected, setSelected] = useState<Set<Locale>>(() => new Set(initial))

	const toggle = useCallback((locale: Locale) => {
		setSelected((current) => {
			const isOn = current.has(locale)
			if (isOn && current.size === 1) return current
			const next = new Set(current)
			if (isOn) next.delete(locale)
			else next.add(locale)
			return next
		})
	}, [])

	const isDirty = !setsEqual(selected, initialSet)

	const commit = useCallback(async () => {
		if (!isDirty) return
		await onSave(Array.from(selected))
	}, [isDirty, onSave, selected])

	const isLastEnabled = selected.size === 1

	return { selected, toggle, isLastEnabled, isDirty, commit }
}
